import { Filter, Search, SlidersHorizontal } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@vnetwork/ui/primitives';
import {
  FilterBuilder,
  type ColumnDef,
  type FilterCondition,
  type FilterField,
} from '@vnetwork/ui/data';

/** A column def whose row shape is left open — reused by both contract lists. */
export type SectionColumn<Row> = ColumnDef<Row, unknown>;

/** Resolve a column's stable toggle key (id, falling back to a string header). */
export const colId = <Row,>(c: SectionColumn<Row>): string =>
  typeof c.id === 'string' ? c.id : String(c.header ?? '');

export interface ContractListToolbarProps<Row> {
  columns: SectionColumn<Row>[];
  filterFields?: FilterField[];
  searchPlaceholder: string;
  query: string;
  onQueryChange: (value: string) => void;
  conditions: FilterCondition[];
  filterOpen: boolean;
  onFilterOpenChange: (open: boolean) => void;
  onApplyFilters: (next: FilterCondition[]) => void;
  visible: Record<string, boolean>;
  onToggleColumn: (id: string, checked: boolean) => void;
}

/**
 * List toolbar shared by the contract lists: a server-side search `Input`
 * (start) plus the Filters `Popover` → `FilterBuilder` and a Display-options
 * column-visibility `DropdownMenu` (end). Purely presentational — all state
 * lives in the parent `ContractListSection`. Mirrors the customer toolbar.
 */
export function ContractListToolbar<Row>({
  columns,
  filterFields,
  searchPlaceholder,
  query,
  onQueryChange,
  conditions,
  filterOpen,
  onFilterOpenChange,
  onApplyFilters,
  visible,
  onToggleColumn,
}: ContractListToolbarProps<Row>) {
  // Conditions fold into a `{ field: [...] }` filters object, so the badge counts
  // DISTINCT constrained fields, not the raw condition count.
  const appliedFieldCount = new Set(conditions.map((c) => c.field)).size;

  return (
    <Toolbar
      style={{ marginBottom: 0 }}
      start={
        <Input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={searchPlaceholder}
          startSlot={<Search />}
          style={{ maxWidth: 320 }}
        />
      }
      end={
        <>
          {filterFields?.length ? (
            <Popover open={filterOpen} onOpenChange={onFilterOpenChange}>
              <PopoverTrigger asChild>
                <Button variant="secondary" startIcon={<Filter size={15} />}>
                  Filters{' '}
                  {appliedFieldCount ? <Badge tone="primary">{appliedFieldCount}</Badge> : null}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" style={{ padding: 0 }}>
                <FilterBuilder
                  fields={filterFields}
                  defaultValue={conditions.length ? conditions : undefined}
                  onApply={onApplyFilters}
                  onClose={() => onFilterOpenChange(false)}
                />
              </PopoverContent>
            </Popover>
          ) : null}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>
                Display options
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
              {columns.map((c) => {
                const id = colId(c);
                return (
                  <DropdownMenuCheckboxItem
                    key={id}
                    checked={visible[id] !== false}
                    onCheckedChange={(v) => onToggleColumn(id, v === true)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {typeof c.header === 'string' ? c.header : id}
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      }
    />
  );
}
